/**
 * Portal guide — assembler of the feature registry.
 *
 * The area files under `registry/` are the hand-maintained source; here they are only
 * assembled, sorted and made accessible. Whoever adds, renames or removes an area, tab or
 * create button updates the registry along with it (see CLAUDE.md, documentation duty) and
 * then syncs into tri-server (`npm run guide:sync`).
 */
import administrationFeatures from './registry/administration.json';
import authFeatures from './registry/auth.json';
import cbosFeatures from './registry/cbos.json';
import chatFeatures from './registry/chat.json';
import entityFeatures from './registry/entity.json';
import settingsFeatures from './registry/settings.json';
import shellFeatures from './registry/shell.json';
import { canonicalJson, sha256Hex } from './fingerprint';
import type { PortalFeature } from './types';

const AREA_FILES: ReadonlyArray<readonly [string, unknown]> = [
  ['administration', administrationFeatures],
  ['auth', authFeatures],
  ['cbos', cbosFeatures],
  ['chat', chatFeatures],
  ['entity', entityFeatures],
  ['settings', settingsFeatures],
  ['shell', shellFeatures],
];

function byId(left: PortalFeature, right: PortalFeature): number {
  if (left.id === right.id) return 0;
  return left.id < right.id ? -1 : 1;
}

/** All shippable entries, ascending by `id`. */
export const PORTAL_FEATURES: PortalFeature[] = AREA_FILES.reduce<PortalFeature[]>(
  (all, [, entries]) => all.concat(entries as PortalFeature[]),
  [],
).sort(byId);

/** Which area file contributes which entries — for guards and translation runs. */
export const PORTAL_FEATURE_AREAS: ReadonlyArray<{ area: string; ids: string[] }> = AREA_FILES.map(
  ([area, entries]) => ({ area, ids: (entries as PortalFeature[]).map((feature) => feature.id) }),
);

const index = new Map<string, PortalFeature>();
PORTAL_FEATURES.forEach((feature) => index.set(feature.id, feature));

export function findFeature(id: string): PortalFeature | undefined {
  return index.get(id);
}

/**
 * Entries grouped by canonical address. The key is the path without the query — that is what
 * the page context (`useLocation().pathname`) provides; the query lives on the entry itself.
 */
export function featuresByRoute(): Map<string, PortalFeature[]> {
  const grouped = new Map<string, PortalFeature[]>();
  PORTAL_FEATURES.forEach((feature) => {
    const queryStart = feature.route.indexOf('?');
    const pathname = queryStart < 0 ? feature.route : feature.route.slice(0, queryStart);
    const bucket = grouped.get(pathname);
    if (bucket) bucket.push(feature);
    else grouped.set(pathname, [feature]);
  });
  return grouped;
}

let fingerprint: string | null = null;

/**
 * sha256 over the canonical JSON of the whole registry (contract §1). Same content → same
 * value, in browser and Node. The sync script from package A2 writes it to `registry.meta.json`
 * so that portal and server can be compared.
 */
export function registryFingerprint(): string {
  if (fingerprint === null) fingerprint = sha256Hex(canonicalJson(PORTAL_FEATURES));
  return fingerprint;
}
